/**
 * DJB Battleship - Theme System
 * Handles light/dark mode toggle and faction theming
 */

const ThemeManager = {
    STORAGE_KEY_THEME: 'djb-theme',
    STORAGE_KEY_FACTION: 'djb-faction',

    /**
     * Initialize theme from saved preference or system preference
     */
    init() {
        this.initTheme();
        this.initFaction();
        this.listenForSystemChanges();
    },

    /**
     * Initialize theme (dark/light)
     */
    initTheme() {
        const saved = localStorage.getItem(this.STORAGE_KEY_THEME);
        if (saved) {
            document.documentElement.setAttribute('data-theme', saved);
        } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
            document.documentElement.setAttribute('data-theme', 'light');
        } else {
            document.documentElement.setAttribute('data-theme', 'dark');
        }
    },

    /**
     * Initialize faction from saved preference
     */
    initFaction() {
        const saved = localStorage.getItem(this.STORAGE_KEY_FACTION);
        if (saved) {
            document.documentElement.setAttribute('data-faction', saved);
        }
    },

    /**
     * Get current theme
     * @returns {string} 'dark' or 'light'
     */
    getTheme() {
        return document.documentElement.getAttribute('data-theme') || 'dark';
    },

    /**
     * Set theme
     * @param {string} theme - 'dark' or 'light'
     */
    setTheme(theme) {
        document.documentElement.setAttribute('data-theme', theme);
        try { localStorage.setItem(this.STORAGE_KEY_THEME, theme); } catch { /* private browsing / quota */ }
    },

    /**
     * Toggle between dark and light themes
     * @returns {string} The new theme
     */
    toggleTheme() {
        const current = this.getTheme();
        const next = current === 'light' ? 'dark' : 'light';
        this.setTheme(next);
        return next;
    },

    /**
     * Get current faction
     * @returns {string|null} Faction ID or null
     */
    getFaction() {
        return document.documentElement.getAttribute('data-faction');
    },

    /**
     * Set faction
     * @param {string} factionId - Faction ID (e.g., 'arcona', 'vizsla')
     */
    setFaction(factionId) {
        if (factionId) {
            document.documentElement.setAttribute('data-faction', factionId);
            try { localStorage.setItem(this.STORAGE_KEY_FACTION, factionId); } catch { /* private browsing / quota */ }
        } else {
            document.documentElement.removeAttribute('data-faction');
            localStorage.removeItem(this.STORAGE_KEY_FACTION);
        }
    },

    /**
     * Clear faction (use default theme colors)
     */
    clearFaction() {
        document.documentElement.removeAttribute('data-faction');
        localStorage.removeItem(this.STORAGE_KEY_FACTION);
    },

    /**
     * Listen for system preference changes
     */
    listenForSystemChanges() {
        if (window.matchMedia) {
            window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', (e) => {
                // Only auto-switch if user hasn't set a preference
                if (!localStorage.getItem(this.STORAGE_KEY_THEME)) {
                    const newTheme = e.matches ? 'light' : 'dark';
                    document.documentElement.setAttribute('data-theme', newTheme);
                }
            });
        }
    },

    /**
     * Create theme toggle UI element using safe DOM methods
     * @returns {HTMLElement} Theme toggle container
     */
    createToggleUI() {
        const container = document.createElement('div');
        container.className = 'theme-toggle';

        // Moon icon
        const moonIcon = document.createElement('span');
        moonIcon.className = 'toggle-icon';
        moonIcon.textContent = '\u263E'; // ☾

        // Dark label
        const darkLabel = document.createElement('span');
        darkLabel.className = 'toggle-label';
        darkLabel.setAttribute('data-theme-label', 'dark');
        darkLabel.textContent = 'Dark';

        // Toggle switch
        const toggle = document.createElement('div');
        toggle.className = 'toggle-switch';
        toggle.id = 'theme-switch';

        // Light label
        const lightLabel = document.createElement('span');
        lightLabel.className = 'toggle-label';
        lightLabel.setAttribute('data-theme-label', 'light');
        lightLabel.textContent = 'Light';

        // Sun icon
        const sunIcon = document.createElement('span');
        sunIcon.className = 'toggle-icon';
        sunIcon.textContent = '\u2600'; // ☀

        container.appendChild(moonIcon);
        container.appendChild(darkLabel);
        container.appendChild(toggle);
        container.appendChild(lightLabel);
        container.appendChild(sunIcon);

        toggle.addEventListener('click', () => {
            this.toggleTheme();
            this.updateToggleUI(container);
        });

        this.updateToggleUI(container);
        return container;
    },

    /**
     * Update toggle UI to reflect current theme
     * @param {HTMLElement} container - Toggle container element
     */
    updateToggleUI(container) {
        const theme = this.getTheme();
        const labels = container.querySelectorAll('.toggle-label');
        labels.forEach(label => {
            if (label.getAttribute('data-theme-label') === theme) {
                label.classList.add('active');
            } else {
                label.classList.remove('active');
            }
        });
    },

    /**
     * Create theme toggle UI into a container element
     * @param {HTMLElement} container - Container to append toggle to
     */
    createToggle(container) {
        const toggleUI = this.createToggleUI();
        container.appendChild(toggleUI);
    }
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ThemeManager.init());
} else {
    ThemeManager.init();
}

// Expose globally for inline scripts in HTML pages
window.ThemeManager = ThemeManager;

// ES module export
export { ThemeManager };
